import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, from, mergeMap, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import { injectReportsComponentHttpClient } from '@core/reports-contracts';

import { ReportsComponentsHttpClient } from './components.http-client';
import { ReportsComponentsActions, ReportsComponentsSelectors } from './components.store';

@Injectable()
export class ReportsComponentsEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly http =
    injectReportsComponentHttpClient({
      optional: true,
    }) ?? inject(ReportsComponentsHttpClient);

  public effects = createEffectFactory(ReportsComponentsActions, this.actions$);

  public createMany$ = this.effects(ReportsComponentsActions.createMany, ({ payload }) =>
    from(payload.componentsData).pipe(
      map(({ component, correlationId }) =>
        ReportsComponentsActions.createComponent({
          payload: {
            data: {
              dashboardId: payload.dashboardId,
              component,
            },
            correlationId,
          },
        }),
      ),
    ),
  );

  public createOneOptimistic$ = this.effects(
    ReportsComponentsActions.createComponent,
    ({ payload }) =>
      this.http.createOne(payload.data.dashboardId, payload.data.component).pipe(
        map(({ component }) =>
          ReportsComponentsActions.createOneOptimisticSuccess({
            payload: {
              correlationId: payload.correlationId,
              data: component,
            },
          }),
        ),
        catchError(error =>
          of(
            ReportsComponentsActions.createOneOptimisticFailed({
              payload: {
                correlationId: payload.correlationId,
                // expects ReportDashboardComponent type, but its ReportDashboardComponentCreate
                data: <any>payload.data.component,
                error,
                dashboardId: payload.data.dashboardId,
              },
            }),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public duplicateOneOptimistic$ = this.effects(
    ReportsComponentsActions.duplicateComponent,
    ({ payload }) =>
      this.http.createOne(payload.data.dashboardId, payload.data.component).pipe(
        map(({ component }) =>
          ReportsComponentsActions.duplicateComponentSuccess({
            payload: {
              correlationId: payload.correlationId,
              data: component,
            },
          }),
        ),
        catchError(error =>
          of(
            ReportsComponentsActions.duplicateComponentFailed({
              payload: {
                correlationId: payload.correlationId,
                // expects ReportDashboardComponent type, but its ReportDashboardComponentCreate
                data: <any>payload.data.component,
                error,
              },
            }),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public updateOneOptimistic$ = this.effects(
    ReportsComponentsActions.updateComponent,
    ({ payload }) =>
      (payload.data.component.id
        ? of(payload.data.component)
        : this.store.pipe(
            select(ReportsComponentsSelectors.byId(payload.data.component.localId)),
            filterBoolean,
            filter(component => component?.id !== undefined),
            take(1),
          )
      ).pipe(
        switchMap(({ id }) =>
          this.http.updateOne(payload.data.dashboardId, { ...payload.data.component, id }).pipe(
            map(({ component }) =>
              ReportsComponentsActions.updateOneOptimisticSuccess({
                payload: {
                  correlationId: payload.correlationId,
                  data: component,
                },
              }),
            ),
            catchError(error =>
              of(
                ReportsComponentsActions.updateOneOptimisticFailed({
                  payload: {
                    correlationId: payload.correlationId,
                    data: { ...payload.data.component, id },
                    error,
                  },
                }),
              ),
            ),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public updateGridPropsOptimistic$ = this.effects(
    ReportsComponentsActions.updateGridPropsOptimistic,
    ({ payload }) =>
      combineLatest(
        payload.data.components.map(component =>
          component.id
            ? of(component)
            : this.store.pipe(
                select(ReportsComponentsSelectors.byId(component.localId)),
                filter(component => component?.id !== undefined),
                take(1),
                map(stored => ({ ...component, id: stored!.id })),
              ),
        ),
      ).pipe(
        take(1),
        switchMap(components =>
          this.http.updateGridProps(payload.data.dashboardId, components).pipe(
            map(data =>
              ReportsComponentsActions.updateGridPropsOptimisticSuccess({
                payload: { data: data.components, correlationId: payload.correlationId },
              }),
            ),
            catchError(error =>
              of(
                ReportsComponentsActions.updateGridPropsOptimisticFailed({
                  payload: {
                    correlationId: payload.correlationId,
                    error,
                  },
                }),
              ),
            ),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public removeOneOptimistic$ = this.effects(
    ReportsComponentsActions.removeComponent,
    ({ payload }) =>
      this.http.removeOne(payload.data.dashboardId, payload.data.component.id).pipe(
        map(({ component }) =>
          ReportsComponentsActions.removeOneOptimisticSuccess({
            payload: {
              data: component,
            },
          }),
        ),
        catchError(error =>
          of(
            ReportsComponentsActions.removeOneOptimisticFailed({
              payload: {
                data: payload.data.component.id,
                error,
              },
            }),
          ),
        ),
      ),
    { customMapOperator: mergeMap },
  );
}
